import { Component, type ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import OffersListComponent, { type OffersListIndexProps } from '@cx-modules/offers-list';

import { OffersClickoutGuard } from '../offers-clickout';

// Shared OffersList wrapper (NGF-6397), single source of truth for RR7 atlas and atlas-astro. Renders
// the @cx-modules/offers-list module with the app-agnostic pre-configuration RR7 used to apply inline:
// repub-specific filtersThreshold, the clickout guard, the data-* analytics attributes, and an
// ErrorBoundary + Suspense. Two things were host-decoupled in the extraction:
//   - `clickoutServiceUrl` is no longer read from the global `ENV` here; callers pass it through props
//     (RR7's componentMap / atlas-astro's island load() inject the bare CLICKOUT_URL). This preserves
//     the NGF-6268 "bare URL wins over the clickout spread's /offer/clickout value" behaviour at the
//     call site.
//   - The error boundary takes an `onError` callback instead of importing @sentry/react-router (which
//     @cx-web/components cannot depend on). RR7 injects `captureException`; atlas-astro (no Sentry yet)
//     injects a console.error shim so failures still surface in the server log / browser console.

export type OffersListProps = Omit<OffersListIndexProps, 'i18n'> & {
  /**
   * Called when the OffersList subtree throws (including the OffersClickoutGuard's deliberate throw on
   * missing/malformed clickout props). RR7 atlas wires this to Sentry's captureException; atlas-astro
   * (no Sentry yet) wires a console.error shim so the failure still surfaces in the log/console.
   */
  onError?: (error: unknown) => void;
};

class OffersListErrorBoundary extends Component<
  { children: ReactNode; onError?: (error: unknown) => void },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    this.props.onError?.(error);
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

export const OffersList = ({ onError, ...props }: OffersListProps) => {
  const { i18n } = useTranslation();

  const filtersThreshold = ['IT'].includes(props.repub) ? 2 : Infinity;

  const preconfiguredProps = {
    ...props,
    filtersThreshold,
    i18n,
  };

  return (
    <OffersListErrorBoundary onError={onError}>
      <Suspense fallback={null}>
        <div
          data-country-code={props.countryCode}
          data-geo-subdivision-code={props.geoSubdivisionCode}
          data-lang={i18n.language}
          data-repub={props.repub}
          data-subdivision-code={props.subdivisionCode}
        >
          <OffersClickoutGuard componentName="OffersList" values={preconfiguredProps}>
            <OffersListComponent {...preconfiguredProps} />
          </OffersClickoutGuard>
        </div>
      </Suspense>
    </OffersListErrorBoundary>
  );
};
